import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  InputAdornment,
  Slider,
  Typography,
  Divider,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { AVAILABLE_MODELS, BEDROCK_MODELS, AvailableModel, BedrockModel } from '../../constants/defaults';
import RagSettingsPanel from './RagSettingsPanel';
import ProjectSettingsImport from './ProjectSettingsImport';

interface EditProjectDialogProps {
  open: boolean;
  loading: boolean;
  editedName: string;
  editedBasePrompt: string;
  editedIntroMessage: string;
  editedModel: AvailableModel | '';
  editedChatType: 'direct' | 'rag';
  editedBedrockModel: BedrockModel | '';
  editedRagVectorDb: string;
  isEditing: boolean;
  onClose: () => void;
  onSave: (settings: {
    name: string;
    base_prompt: string;
    intro_message: string;
    model_name: string;
    chat_type: 'direct' | 'rag';
    rag_vector_db?: string;
    rag_top_k?: number;
    rag_confidence_threshold?: number;
    rag_token_limit?: number;
  }) => void;
  onExportSettings: () => void;
  onImportSettings: (settings: Record<string, string | number>) => void;
  onNameChange: (value: string) => void;
  onBasePromptChange: (value: string) => void;
  onIntroMessageChange: (value: string) => void;
  onModelChange: (value: AvailableModel) => void;
  onChatTypeChange: (value: 'direct' | 'rag') => void;
  onBedrockModelChange: (value: BedrockModel) => void;
  onRagVectorDbChange: (value: string) => void;
}

const EditProjectDialog: React.FC<EditProjectDialogProps> = ({
  open,
  loading,
  editedName,
  editedBasePrompt,
  editedIntroMessage,
  editedModel,
  editedChatType,
  editedBedrockModel,
  editedRagVectorDb,
  isEditing,
  onClose,
  onSave,
  onExportSettings,
  onImportSettings,
  onNameChange,
  onBasePromptChange,
  onIntroMessageChange,
  onModelChange,
  onChatTypeChange,
  onBedrockModelChange,
  onRagVectorDbChange,
}) => {
  // Internally manage RAG settings
  const [editedRagTopK, setEditedRagTopK] = React.useState(3);
  const [editedRagConfidence, setEditedRagConfidence] = React.useState(0.7);
  const [editedRagTokenLimit, setEditedRagTokenLimit] = React.useState(4000);
  const handleChatTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newChatType = e.target.value as 'direct' | 'rag';
    console.log('Changing chat type to:', newChatType);
    onChatTypeChange(newChatType);
    
    // If switching to RAG, set up RAG-specific settings
    if (newChatType === 'rag') {
      // Vector indices will be fetched by RagSettingsPanel
      console.log('Chat type changed to RAG, index selection will be available in RagSettingsPanel');
    }
  };

  const handleRagVectorDbChange = (value: string) => {
    onRagVectorDbChange(value);
    
    // Ensure we're still in RAG mode (defensive programming)
    if (editedChatType !== 'rag') {
      console.log('Ensuring chat type is RAG');
      onChatTypeChange('rag');
    }
  };
  
  const handleSaveClick = () => {
    // Create a settings object with all edited values
    const settings = {
      name: editedName,
      base_prompt: editedBasePrompt,
      intro_message: editedIntroMessage,
      model_name: editedChatType === 'direct' ? editedModel : editedBedrockModel,
      chat_type: editedChatType,
    };
    
    // Add RAG-specific settings if in RAG mode
    if (editedChatType === 'rag') {
      Object.assign(settings, {
        rag_vector_db: editedRagVectorDb,
        rag_top_k: editedRagTopK,
        rag_confidence_threshold: editedRagConfidence,
        rag_token_limit: editedRagTokenLimit
      });
    }
    
    onSave(settings);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      aria-labelledby="edit-project-dialog-title"
      PaperProps={{
        sx: {
          '& .MuiDialogTitle-root': {
            padding: '16px 24px 8px 24px',
          },
          '& .MuiDialogContent-root': {
            padding: '8px 24px 16px 24px',
            '& .MuiTextField-root': {
              marginTop: '8px',
              marginBottom: '8px',
              '&:last-child': {
                marginBottom: 0
              }
            }
          },
          '& .MuiDialogActions-root': {
            padding: '8px 24px 16px'
          }
        }
      }}
    >
      <DialogTitle id="edit-project-dialog-title">Edit Project</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Project Name"
          fullWidth
          value={editedName}
          onChange={(e) => onNameChange(e.target.value)}
          margin="normal"
        />
        <FormControl component="fieldset" fullWidth margin="normal">
          <Typography variant="subtitle1" gutterBottom>Chat Type</Typography>
          <RadioGroup
            row
            value={editedChatType}
            onChange={handleChatTypeChange}
          >
            <FormControlLabel value="direct" control={<Radio />} label="Direct Chat" />
            <FormControlLabel value="rag" control={<Radio />} label="RAG Chat" />
          </RadioGroup>
        </FormControl>
        {editedChatType === 'direct' ? (
          <TextField
            select
            label="Model"
            fullWidth
            value={editedModel || ''}
            onChange={(e) => onModelChange(e.target.value as AvailableModel)}
            margin="normal"
          >
            {AVAILABLE_MODELS.map((model) => (
              <MenuItem key={model.value} value={model.value}>
                {model.label}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <RagSettingsPanel
            editedRagVectorDb={editedRagVectorDb}
            editedRagTopK={editedRagTopK}
            editedRagConfidence={editedRagConfidence}
            editedRagTokenLimit={editedRagTokenLimit}
            editedBedrockModel={editedBedrockModel}
            onRagVectorDbChange={handleRagVectorDbChange}
            onBedrockModelChange={onBedrockModelChange}
            onRagTopKChange={setEditedRagTopK}
            onRagConfidenceChange={setEditedRagConfidence}
            onRagTokenLimitChange={setEditedRagTokenLimit}
          />
        )}
        <TextField
          label="System Prompt"
          fullWidth
          multiline
          rows={4}
          value={editedBasePrompt}
          onChange={(e) => onBasePromptChange(e.target.value)}
          margin="normal"
        />
        <TextField
          label="Intro Message"
          fullWidth
          multiline
          rows={4}
          value={editedIntroMessage}
          onChange={(e) => onIntroMessageChange(e.target.value)}
          margin="normal"
        />

      </DialogContent>
      <DialogActions>
        <ProjectSettingsImport onImportSuccess={onImportSettings} />
        <Tooltip title="Export Settings">
          <IconButton onClick={onExportSettings}>
            <FileUploadIcon sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
        </Tooltip>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSaveClick} 
          variant="contained" 
          disabled={loading || (editedChatType === 'rag' && !editedRagVectorDb)}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProjectDialog;
